/* eslint-disable react/require-default-props */
import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Stack,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';

import api from '../../../services/api';

const DEFAULT_FORM = {
  name: '',
  brand: '',
  type: 'iBeacon',
  deviceId: '',
  major: 0,
  minor: 0,
  namespace: '',
  instanceId: '',
  rssi: -55,
};

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function validateBeacon(b) {
  if (!b.name || b.name.trim() === '') return 'O nome do beacon é obrigatório.';
  if (!b.brand || b.brand === '') return 'A marca do beacon não pode ser vazia.';
  if (!b.deviceId || !UUID_REGEX.test(b.deviceId.trim())) {
    return 'O UUID do beacon é inválido. Formato esperado: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx';
  }
  const majorNum = Number(b.major);
  const minorNum = Number(b.minor);
  if (!Number.isInteger(majorNum) || majorNum < 0 || majorNum > 65535) {
    return 'Major deve ser um número inteiro entre 0 e 65535.';
  }
  if (!Number.isInteger(minorNum) || minorNum < 0 || minorNum > 65535) {
    return 'Minor deve ser um número inteiro entre 0 e 65535.';
  }
  return null;
}

export default function BeaconFormModal({
  open,
  onClose,
  beaconToEdit,
  onSaved,
}) {
  const isEditMode = !!beaconToEdit;

  const [beacon, setBeacon] = useState(DEFAULT_FORM);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setBeacon(DEFAULT_FORM);
      return;
    }
    if (beaconToEdit) {
      setBeacon({
        name: beaconToEdit.name || '',
        brand: beaconToEdit.brand || '',
        type: beaconToEdit.type || 'iBeacon',
        deviceId: beaconToEdit.deviceId || '',
        major: beaconToEdit.major ?? 0,
        minor: beaconToEdit.minor ?? 0,
        namespace: beaconToEdit.namespace || '',
        instanceId: beaconToEdit.instanceId || '',
        rssi: beaconToEdit.rssi ?? -55,
      });
    }
  }, [open, beaconToEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setBeacon((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validateBeacon(beacon);
    if (err) {
      alert(err);
      return;
    }

    const data = {
      name: beacon.name.trim(),
      brand: beacon.brand,
      type: beacon.type,
      deviceId: beacon.deviceId.trim().toLowerCase(),
      major: Number(beacon.major),
      minor: Number(beacon.minor),
      namespace: beacon.namespace || undefined,
      instanceId: beacon.instanceId || undefined,
      rssi: Number(beacon.rssi),
    };

    setSubmitting(true);
    try {
      const saved = isEditMode
        ? (await api.patch(`/beacons/${beaconToEdit.id}`, data)).data
        : (await api.post('/beacons', data)).data;
      alert(isEditMode ? 'Beacon atualizado com sucesso.' : 'Beacon criado com sucesso.');
      if (onSaved) onSaved(saved);
      onClose();
    } catch (err2) {
      console.error(err2);
      alert(err2?.response?.data?.message || `Erro ao ${isEditMode ? 'atualizar' : 'cadastrar'} o beacon.`);
    } finally {
      setSubmitting(false);
    }
  };

  const title = isEditMode ? 'Editar beacon' : 'Novo beacon';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
            Preencha os dados do dispositivo Bluetooth pra permitir que o app associe a um ponto.
          </Typography>

          <FormControl fullWidth sx={{ my: 1 }}>
            <TextField
              label="Nome"
              type="text"
              name="name"
              value={beacon.name}
              placeholder="Informe o nome do beacon"
              onChange={handleChange}
              required
            />
          </FormControl>

          <FormControl fullWidth sx={{ my: 1 }}>
            <InputLabel id="brand-label">Marca</InputLabel>
            <Select
              labelId="brand-label"
              label="Marca"
              name="brand"
              value={beacon.brand}
              onChange={handleChange}
            >
              <MenuItem value="estimote">Estimote</MenuItem>
              <MenuItem value="kontakt">Kontakt</MenuItem>
              <MenuItem value="other">Outro</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ my: 1 }}>
            <TextField
              label="UUID"
              type="text"
              name="deviceId"
              value={beacon.deviceId}
              placeholder="Informe o UUID do beacon"
              helperText="Ex.: 5a921f29-de3c-e54c-60f7-dde6dd8d0fe8"
              onChange={handleChange}
              required
            />
          </FormControl>

          <FormControl fullWidth sx={{ my: 1 }}>
            <TextField
              label="MAC Address (opcional)"
              type="text"
              name="namespace"
              value={beacon.namespace}
              placeholder="Ex.: 00:FA:B6:0D:4B:08"
              helperText="Fallback quando iBeacon não é reconhecido. Recomendado p/ Kontakt."
              onChange={handleChange}
            />
          </FormControl>

          <Stack direction="row" spacing={1} sx={{ my: 1 }}>
            <FormControl fullWidth>
              <TextField
                label="Major"
                type="number"
                name="major"
                value={beacon.major}
                onChange={handleChange}
                inputProps={{ min: 0, max: 65535 }}
                helperText="Ex.: 2000"
              />
            </FormControl>
            <FormControl fullWidth>
              <TextField
                label="Minor"
                type="number"
                name="minor"
                value={beacon.minor}
                onChange={handleChange}
                inputProps={{ min: 0, max: 65535 }}
                helperText="Ex.: 2000"
              />
            </FormControl>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} disabled={submitting}>Cancelar</Button>
          <Button
            type="submit"
            variant="contained"
            color="success"
            disabled={submitting}
          >
            {(() => {
              if (submitting) return 'Salvando...';
              return isEditMode ? 'Salvar alterações' : 'Cadastrar';
            })()}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

BeaconFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
  beaconToEdit: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    brand: PropTypes.string,
    type: PropTypes.string,
    deviceId: PropTypes.string,
    major: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    minor: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    namespace: PropTypes.string,
    instanceId: PropTypes.string,
    rssi: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
};
